import { push } from 'connected-react-router';
import { SagaIterator } from 'redux-saga';
import { all, call, put, select, take, takeLatest } from 'redux-saga/effects';
import { getApartment, getApartmentMedia, listApartments, listApartmentsSuccess, listApartmentsFail, updateApartmentViewed, emptyBeforeLoginState } from '../actions/apartments';
import { getApartmentAvailability, getApartmentAvailabilitySuccess, getApartmentAvailabilityFail, getApartmentAvailabilitiesByApplicants, getApartmentAvailabilitiesByApplicantsSuccess, getApartmentAvailabilitiesByApplicantsFail } from '../actions/applicantAvailability';
import { getApplication, listApplications, getApartmentApplications } from '../actions/applications';
import { openDialogWithParams } from '../actions/dialogs';
import { navigate } from '../actions/routes';
import { getReserveData, getReserveDataSuccess, getReserveDataFail, getShowings, getShowingsFail, getShowingsSuccess, getApartmentsShowings, getUserShowings } from '../actions/showings';
import { initializeSessionFail, initializeSessionSuccess, rerouteSession, getCurrentUser, listUsers, initializeSession } from '../actions/users';
import { createAction } from '../actions/utils';
import { addPageView } from '../config/googleTagManager';
import { Action } from '../reducers/types';
import { selectApartmentsAsArray, selectBeforeLoginApartment } from '../selectors/apartments';
import { selectApplicationCodeFromPathname, selectContentIdFromPathname, selectShowingFromPathname, selectStepNumberFromPathname, selectNewApplicationStepNumberFromPathname, selectUserIdFromPathname, selectEmailFromPathname, selectCodeFromPathname, selectBeforeLoginStepNumberFromPathname, selectLocationQuery, selectApartmentStatusFromPathname, selectNewApplicationApartmentCodeFromPathname, selectInstantRentStepNumberFromPathname, selectAfterShowingStepNumberFromPathname } from '../selectors/router';
import { selectReserveApartmentId, selectReserveApplicationId, selectReserveStatus, selectReserveInvited } from '../selectors/showings';
import { selectUserIsAuthenticated, selectIsAdminUser, selectSessionInitializationAttempted, selectIsLoadingSession } from '../selectors/users';
import { getSession } from '../services/auth/auth';
import { getHistory } from '../store';
import { ApartmentStatus, BeforeLoginApartment } from '../types/apartments';
import { ApplicationStatus } from '../types/applications';
import { BeforeLoginApartmentStepPageNames } from '../types/beforeLoginSteps';
// import { BeforeLoginApartmentStepPageNames } from '../types/beforeLoginSteps'
import { ApartmentStepPageNames, NewApplicationStepPageNames } from '../types/steps';
import { getEmailConfirmationResult } from '../utils/users';
import { handleInitializeBeforeLoginApartment } from './apartments/beforeLoginApartments';
import { handleGetUser } from './users';
export function* handleNavigate({ payload: path }: Action): SagaIterator {
    yield put(push(path));
}
/** admins need listed users, normal user only needs current user */
function* getCurrentUserOrGetUserByIdForAdmin(): SagaIterator {
    const authenticated = yield select(selectUserIsAuthenticated);
    if (!authenticated) {
        yield take([initializeSessionSuccess, initializeSessionFail]);
    }
    const isAdmin = yield select(selectIsAdminUser);
    if (isAdmin) {
        const userId = yield select(selectUserIdFromPathname);
        yield call(handleGetUser, { payload: userId } as any);
    }
    else if (!isAdmin) {
        yield put(getCurrentUser());
    }
}
/** userId will be missing if initializing is not done */
function* waitForInitializationAndGetCurrentUser(): SagaIterator {
    const authenticated = yield select(selectUserIsAuthenticated);
    if (!authenticated) {
        yield take([initializeSessionSuccess, initializeSessionFail]);
    }
    yield put(getCurrentUser());
}
/** if no apartments, automatically open create form */
function* handleRerouteSession(): SagaIterator {
    const isAdmin = yield select(selectIsAdminUser);
    if (isAdmin) {
        return yield call(getHistory().push, '/adminApartments/marketing' as any);
    }
    yield put(listApartments());
    yield take([listApartmentsSuccess, listApartmentsFail]);
    const apartments = yield select(selectApartmentsAsArray);
    if (apartments && apartments.length > 0) {
        const marketingApartments = apartments.filter((apartment: any) => apartment.status === ApartmentStatus.marketing) || [];
        if (marketingApartments.length === 1) {
            const apartment = marketingApartments[0];
            return yield call(getHistory().push, `/users/${apartment.userId}/apartments/${apartment.apartmentId}/overview` as any);
        }
        return yield call(getHistory().push, `/apartments` as any);
    }
    return yield call(getHistory().push, '/apartments' as any);
}
function* handleLocationChange({ payload: { location: { pathname }, action: locationAction } }: any): SagaIterator {
    window.scrollTo(0, 0);
    const userId = yield select(selectUserIdFromPathname);
    const isAdmin = yield select(selectIsAdminUser);
    const applicationId = yield select(selectUserIdFromPathname);
    const apartmentId = yield select(selectContentIdFromPathname);
    const showingId = yield select(selectShowingFromPathname);
    const step = yield select(selectStepNumberFromPathname);
    const newApplicationStep = yield select(selectNewApplicationStepNumberFromPathname);
    const newApplicationApartmentCode = yield select(selectNewApplicationApartmentCodeFromPathname);
    const beforeLoginStep = yield select(selectBeforeLoginStepNumberFromPathname);
    const instantRentStep = yield select(selectInstantRentStepNumberFromPathname);
    const afterShowingStep = yield select(selectAfterShowingStepNumberFromPathname);
    const applicationCode = yield select(selectApplicationCodeFromPathname);
    const email = yield select(selectEmailFromPathname);
    const apartmentStatus = yield select(selectApartmentStatusFromPathname);
    const code = yield select(selectCodeFromPathname);
    const sessionInitializationAttempted = yield select(selectSessionInitializationAttempted);
    const query = yield select(selectLocationQuery);
    let status;
    let invitedShowings;
    const isLoadingSession = yield select(selectIsLoadingSession);
    if (!isLoadingSession && !sessionInitializationAttempted) {
        yield put(initializeSession());
    }
    switch (pathname) {
        case '/':
        case '': {
            const session = yield call(getSession);
            if (session) {
                const authenticated = yield select(selectUserIsAuthenticated);
                if (!authenticated) {
                    yield take([initializeSessionSuccess, initializeSessionFail]);
                }
                yield put(rerouteSession());
            }
            if (!session) {
                addPageView(pathname, 'Front page');
            }
            if (query?.emailConfirmationResult) {
                const emailConfirmationResult = getEmailConfirmationResult(query?.emailConfirmationResult);
                yield put(openDialogWithParams({
                    id: 'EmailConfirmationResultDialog',
                    params: emailConfirmationResult
                }));
            }
            break;
        }
        case '/adminApartments':
        case '/adminApartments/':
        case `/adminApartments/${apartmentStatus}`:
            yield put(listApartments(apartmentStatus));
            yield put(listApplications());
            yield put(listUsers());
            yield take([listApartmentsSuccess]);
            yield put(getApartmentsShowings());
            break;
        case `/new-application/${newApplicationApartmentCode}`:
        case `/new-application/${newApplicationApartmentCode}/`:
            addPageView('/new-application', NewApplicationStepPageNames[0]);
            break;
        case `/new-application/${newApplicationApartmentCode}/steps/${newApplicationStep}`:
        case `/new-application/${newApplicationApartmentCode}/steps/${newApplicationStep}/`: {
            const newApplicationFormValues = yield select((state) => state?.form?.newApplication?.values);
            if (newApplicationStep > 0 && !newApplicationFormValues) {
                yield put(navigate(`/new-application/${newApplicationApartmentCode}`));
                break;
            }
            addPageView(`/new-application/steps/${newApplicationStep}`, (NewApplicationStepPageNames as any)[newApplicationStep]);
            break;
        }
        case '/apartments':
        case '/apartments/':
            yield put(listApartments());
            yield put(listApplications());
            yield put(getUserShowings());
            addPageView(pathname, 'Apartments list');
            break;
        case `/users/${userId}/apartments/${apartmentId}/applications`:
        case `/users/${userId}/apartments/${apartmentId}/applications/`:
            yield put(getShowings({ userId, apartmentId }));
            yield put(getApartment({ userId, apartmentId }));
            yield take([getShowingsSuccess, getShowingsFail]);
            yield put(getApartmentApplications({ userId, apartmentId }));
            yield put(getApartmentAvailability({ apartmentId }));
            yield take([
                getApartmentAvailabilitySuccess,
                getApartmentAvailabilityFail
            ]);
            if (!isAdmin) {
                yield put(updateApartmentViewed({ userId, apartmentId }));
            }
            addPageView('/applications', 'Landlord - Invite applicants');
            break;
        case `/users/${userId}/apartments/${apartmentId}/showings`:
        case `/users/${userId}/apartments/${apartmentId}/showings/`:
            yield put(getApartment({ userId, apartmentId }));
            yield put(getShowings({ userId, apartmentId }));
            break;
        case `/users/${userId}/apartments/${apartmentId}/showings/open`:
        case `/users/${userId}/apartments/${apartmentId}/showings/open/`:
            yield put(getApartment({ userId, apartmentId }));
            addPageView('/showings/open', 'Landlord - Open showing');
            break;
        case `/users/${userId}/apartments/${apartmentId}/showings/invite`:
        case `/users/${userId}/apartments/${apartmentId}/showings/invite/`:
            addPageView('/showings', 'Landlord - Create showings view');
            yield put(getApartment({ userId, apartmentId }));
            yield put(getShowings({ userId, apartmentId }));
            break;
        case `/users/${userId}/apartments/${apartmentId}/showings/${showingId}/edit`:
        case `/users/${userId}/apartments/${apartmentId}/showings/${showingId}/edit/`:
            yield put(getShowings({ userId, apartmentId }));
            yield put(getApartment({ userId, apartmentId }));
            yield take([getShowingsSuccess, getShowingsFail]);
            yield put(getApartmentApplications({ userId, apartmentId }));
            addPageView('/applications', 'Landlord - Invite applicants');
            break;
        case `/users/${userId}/apartments/${apartmentId}/statistic`:
        case `/users/${userId}/apartments/${apartmentId}/statistic/`:
            yield put(getApartment({ userId, apartmentId }));
            addPageView('/statistic', 'Apartment statistics');
            break;
        case `/users/${userId}/apartments/${apartmentId}/media`:
        case `/users/${userId}/apartments/${apartmentId}/media/`:
            yield put(getApartment({ userId, apartmentId }));
            yield put(getApartmentMedia(apartmentId));
            addPageView('/media', 'Apartment media');
            break;
        case `/users/${userId}/apartments/${apartmentId}/steps/${step}`:
        case `/users/${userId}/apartments/${apartmentId}/steps/${step}/`:
            if (locationAction === 'POP' && step > 0) {
                yield put(navigate(`/users/${userId}/apartments/${apartmentId}/steps/0`));
                break;
            }
            yield put(getApartment({ userId, apartmentId }));
            if (step === 0) {
                yield call(getCurrentUserOrGetUserByIdForAdmin);
            }
            if (step === 11) {
                yield put(getApartmentMedia(apartmentId));
            }
            addPageView(`/steps/${step}`, (ApartmentStepPageNames as any)[step]);
            break;
        case `/users/${userId}/apartments/${apartmentId}/overview`:
        case `/users/${userId}/apartments/${apartmentId}/overview/`:
            yield put(getApartment({ userId, apartmentId }));
            yield put(getApartmentApplications({ userId, apartmentId }));
            yield put(getApartmentMedia(apartmentId));
            yield call(getCurrentUserOrGetUserByIdForAdmin);
            addPageView('/overview', 'Apartment overview');
            break;
        case `/application/${applicationId}/apartments/${apartmentId}/edit`:
        case `/application/${applicationId}/apartments/${apartmentId}/edit/`:
            yield take([initializeSessionSuccess, initializeSessionFail]);
            yield put(getApplication({ applicationId, apartmentId }));
            break;
        case `/reserve/${applicationCode}`:
        case `/reserve/${applicationCode}/`:
            yield put(getReserveData(applicationCode));
            yield take([getReserveDataSuccess, getReserveDataFail]);
            status = yield select(selectReserveStatus);
            invitedShowings = yield select(selectReserveInvited);
            if (status === ApplicationStatus.noAvailableTimes ||
                status === ApplicationStatus.created ||
                (status === ApplicationStatus.accepted && !invitedShowings.length)) {
                yield put(navigate(`/reserve/${applicationCode}/suggest`));
            }
            else {
                addPageView(`/reserve`, 'Applicant - Reserve viewing');
            }
            break;
        case `/reserve/${applicationCode}/suggest`:
        case `/reserve/${applicationCode}/suggest/`:
            yield put(getReserveData(applicationCode));
            yield take([getReserveDataSuccess, getReserveDataFail]);
            status = yield select(selectReserveStatus);
            invitedShowings = yield select(selectReserveInvited);
            if ((status === ApplicationStatus.accepted && !!invitedShowings.length) ||
                status === ApplicationStatus.showingReserved ||
                status === ApplicationStatus.notInterested ||
                status === ApplicationStatus.rejected) {
                yield put(navigate(`/reserve/${applicationCode}`));
            }
            else {
                const apartmentIdFromReserve = yield select(selectReserveApartmentId);
                const applicationIdFromReserve = yield select(selectReserveApplicationId);
                yield put(getApartmentAvailabilitiesByApplicants({
                    apartmentId: apartmentIdFromReserve,
                    applicationId: applicationIdFromReserve
                }));
                yield take([
                    getApartmentAvailabilitiesByApplicantsSuccess,
                    getApartmentAvailabilitiesByApplicantsFail
                ]);
                addPageView(`/suggest`, 'Applicant - Suggest viewing timeslots');
            }
            break;
        case '/profile':
        case '/profile/':
            addPageView(pathname, 'User profile view');
            yield call(waitForInitializationAndGetCurrentUser);
            break;
        /** Can be used by admins etc */
        case '/hakijaprofiili':
        case '/hakijaprofiili/':
            addPageView(pathname, 'Sign up');
            break;
        /** Route old signup URL to new apartment creation */
        case '/signup':
        case '/signup/':
            addPageView(pathname, 'Sign up');
            break;
        case '/create/apartment':
        case '/create/apartment/':
            addPageView(pathname, 'Create apartment before login');
            yield call(handleInitializeBeforeLoginApartment);
            break;
        case `/create/apartment/steps/${beforeLoginStep}`:
        case `/create/apartment/steps/${beforeLoginStep}/`: {
            if (locationAction === 'POP' && beforeLoginStep > 0) {
                yield put(navigate(`/create/apartment/steps/0`));
                break;
            }
            const beforeLoginApartment: BeforeLoginApartment | undefined = yield select(selectBeforeLoginApartment);
            /** wait to check if user is logged in before creating apartment  */
            if (!sessionInitializationAttempted) {
                yield take([initializeSessionSuccess, initializeSessionFail]);
            }
            const isLoggedIn = yield select(selectUserIsAuthenticated);
            /** user can edit apartment if they log in on confirmation and there is an error, so apartment is not lost */
            if (isLoggedIn && !beforeLoginApartment?.metadata?.information) {
                /**
                 * Logged in users are moved to apartments list so they don't create empty apartments.
                 * Unless sign in happens at confirmation (if errors happen, they can still fix issues)
                 * */
                yield put(emptyBeforeLoginState());
                yield put(navigate(`/apartments`));
                break;
            }
            // BEFORE LOGIN REDIRECTION
            if (!beforeLoginApartment) {
                /** if user gets logged in and there is an error in a form field, don't redirect here */
                yield put(navigate(`/create/apartment`));
            }
            else if (beforeLoginStep > 8 && !beforeLoginApartment.information) {
                yield put(navigate(`/create/apartment/steps/0`));
            }
            else {
                addPageView(pathname, (BeforeLoginApartmentStepPageNames as any)[beforeLoginStep]);
            }
            break;
        }
        // case '/instant-rent':
        // case '/instant-rent/':
        //   break
        case `/instant-rent/steps/${instantRentStep}`:
        case `/instant-rent/steps/${instantRentStep}/`: {
            addPageView(pathname, 'Instant Rent');
            yield call(handleInitializeBeforeLoginApartment);
            const beforeLoginApartment: BeforeLoginApartment | undefined = yield select(selectBeforeLoginApartment);
            /** wait to check if user is logged in before creating apartment  */
            if (!sessionInitializationAttempted) {
                yield take([initializeSessionSuccess, initializeSessionFail]);
            }
            const isLoggedIn = yield select(selectUserIsAuthenticated);
            /** user can edit apartment if they log in on confirmation and there is an error, so apartment is not lost */
            if (isLoggedIn && !beforeLoginApartment?.metadata?.information) {
                /**
                 * Logged in users are moved to apartments list so they don't create empty apartments.
                 * Unless sign in happens at confirmation (if errors happen, they can still fix issues)
                 * */
                yield put(emptyBeforeLoginState());
                yield put(navigate(`/apartments`));
                break;
            }
            // BEFORE LOGIN REDIRECTION
            // if (!beforeLoginApartment) {
            //   /** if user gets logged in and there is an error in a form field, don't redirect here */
            //   yield put(navigate(`/instant-rent`))
            // } else if (instantRentStep > 8 && !beforeLoginApartment.information) {
            //   yield put(navigate(`/instant-rent/steps/0`))
            // } else {
            //   addPageView(
            //     pathname,
            //     BeforeLoginApartmentStepPageNames[instantRentStep]
            //   )
            // }
            break;
        }
        case `/aftershowing/steps/${afterShowingStep}`:
        case `/aftershowing/steps/${afterShowingStep}/`: {
            addPageView(pathname, 'After Showing');
            yield call(handleInitializeBeforeLoginApartment);
            const beforeLoginApartment: BeforeLoginApartment | undefined = yield select(selectBeforeLoginApartment);
            /** wait to check if user is logged in before creating apartment  */
            if (!sessionInitializationAttempted) {
                yield take([initializeSessionSuccess, initializeSessionFail]);
            }
            const isLoggedIn = yield select(selectUserIsAuthenticated);
            /** user can edit apartment if they log in on confirmation and there is an error, so apartment is not lost */
            if (isLoggedIn && !beforeLoginApartment?.metadata?.information) {
                /**
                 * Logged in users are moved to apartments list so they don't create empty apartments.
                 * Unless sign in happens at confirmation (if errors happen, they can still fix issues)
                 * */
                yield put(emptyBeforeLoginState());
                yield put(navigate(`/apartments`));
                break;
            }
            // BEFORE LOGIN REDIRECTION
            // if (!beforeLoginApartment) {
            //   /** if user gets logged in and there is an error in a form field, don't redirect here */
            //   yield put(navigate(`/instant-rent`))
            // } else if (instantRentStep > 8 && !beforeLoginApartment.information) {
            //   yield put(navigate(`/instant-rent/steps/0`))
            // } else {
            //   addPageView(
            //     pathname,
            //     BeforeLoginApartmentStepPageNames[instantRentStep]
            //   )
            // }
            break;
        }
        case '/forgotpassword/request':
        case '/forgotpassword/request/':
            addPageView(pathname, 'Reset password - Request reset code');
            break;
        case `/forgotpassword/reset/${email}/${code}`:
        case `/forgotpassword/reset/${email}/${code}/`:
            addPageView('/forgotpassword/reset', 'Reset password');
            break;
        default:
            addPageView(pathname, 'Unknown page or Title undefined');
            break;
    }
}
function* watchRoutesActions(): SagaIterator {
    yield all([
        takeLatest(navigate, handleNavigate),
        takeLatest(rerouteSession, handleRerouteSession),
        takeLatest(createAction('@@router', 'LOCATION_CHANGE'), handleLocationChange)
    ]);
}
export default [watchRoutesActions];
